import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, FlameIcon } from 'lucide-react';
interface PainPointsProps {
  onCtaClick: () => void;
}
export function PainPoints({
  onCtaClick
}: PainPointsProps) {
  const pains = ['Te acostás pensando en todo lo que no resolviste', 'Las discusiones en casa empiezan por pavadas y terminan en portazos', 'La plata entra y se va — no sabés bien a dónde', 'Sentís el ambiente pesado apenas entrás a tu casa', 'Cansancio mental constante, como si cargaras algo invisible', 'El desorden físico te irrita pero no tenés energía para ordenar', 'Sentís que no avanzás, que todo está trabado', 'Ansiedad que no podés explicar pero que está siempre ahí', 'Convivís con alguien que no acompaña y eso te desgasta', 'Sentís que perdiste autoridad sobre tu propia vida'];
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const toggle = (idx: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };
  const count = selected.size;
  const getMessage = () => {
    if (count === 0) return null;
    if (count === 1) return {
      text: 'Seguí marcando los que sientas que te hablan a vos...',
      bg: 'bg-card-bg border border-border-gold',
      textColor: 'text-text-secondary',
      emoji: '👆'
    };
    if (count === 2) return {
      text: 'Ya van 2... si marcás uno más, tenemos algo que decirte.',
      bg: 'bg-card-bg border border-border-gold',
      textColor: 'text-text-secondary',
      emoji: '👀'
    };
    if (count <= 5) return {
      text: 'Esto sin dudas es para vos. Este sistema fue diseñado exactamente para lo que estás viviendo.',
      bg: 'bg-gold/15 border-2 border-gold/40',
      textColor: 'text-gold',
      emoji: '🔥'
    };
    if (count <= 8) return {
      text: `Marcaste ${count}. No es casualidad que estés acá. Hay un sistema esperándote.`,
      bg: 'bg-gold/15 border-2 border-gold/40',
      textColor: 'text-gold',
      emoji: '✨'
    };
    return {
      text: `Marcaste ${count} de 10. Necesitás esto. Y lo bueno es que ya existe una guía paso a paso para salir de ahí.`,
      bg: 'bg-gold/20 border-2 border-gold',
      textColor: 'text-gold',
      emoji: '💛'
    };
  };
  const message = getMessage();
  // Progress bar percentage
  const progress = count / pains.length * 100;
  return <section className="py-10 md:py-20 px-4 bg-primary-bg relative" data-id="element-199">
      <div className="max-w-4xl mx-auto" data-id="element-200">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} className="text-center mb-6 md:mb-8" data-id="element-201">
          <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary mb-2 md:mb-3" data-id="element-202">
            ¿Te pasa esto?
          </h2>
          <p className="font-body-serif text-sm md:text-lg text-text-secondary" data-id="element-203">
            Tocá cada frase que sientas que te habla a vos.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-2.5 mb-4 md:mb-5" data-id="element-204">
          {pains.map((pain, idx) => {
          const isSelected = selected.has(idx);
          return <button key={idx} onClick={() => toggle(idx)} className={`flex items-start space-x-2.5 p-3 md:p-3.5 rounded-lg md:rounded-xl border text-left transition-all duration-200 ${isSelected ? 'bg-gold/15 border-gold shadow-[0_0_12px_rgba(184,134,11,0.15)] scale-[1.01]' : 'bg-card-bg border-border-gold shadow-sm hover:border-gold/40'}`} data-id="element-205">
                <div className={`w-5 h-5 md:w-5.5 md:h-5.5 rounded-md border-2 shrink-0 mt-0.5 flex items-center justify-center transition-all duration-200 ${isSelected ? 'bg-gold border-gold shadow-[0_0_8px_rgba(184,134,11,0.3)]' : 'border-text-muted/30 bg-transparent'}`} data-id="element-206">
                  {isSelected && <CheckIcon className="w-3 h-3 md:w-3.5 md:h-3.5 text-white" data-id="element-207" />}
                </div>
                <p className={`text-sm md:text-base leading-snug font-medium transition-colors ${isSelected ? 'text-text-primary' : 'text-text-secondary'}`} data-id="element-208">
                  {pain}
                </p>
              </button>;
        })}
        </div>

        {/* Progress Counter */}
        <div className="mb-4 md:mb-5" data-id="element-209">
          <div className="max-w-xs mx-auto" data-id="element-210">
            {/* Progress bar */}
            <div className="h-2.5 rounded-full bg-primary-bg border border-border-gold overflow-hidden mb-2" data-id="element-211">
              <motion.div className={`h-full rounded-full transition-colors duration-300 ${count === 0 ? 'bg-text-muted/20' : count < 3 ? 'bg-gold/50' : 'bg-gold'}`} initial={{
              width: 0
            }} animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.4,
              ease: 'easeOut'
            }} data-id="element-212" />
            </div>
            <div className="flex items-center justify-center gap-1.5" data-id="element-213">
              {count >= 3 && <FlameIcon className="w-4 h-4 text-gold" data-id="element-214" />}
              <span className={`text-sm md:text-base font-bold ${count === 0 ? 'text-text-muted' : count < 3 ? 'text-text-secondary' : 'text-gold'}`} data-id="element-215">
                {count} de {pains.length}
              </span>
              {count >= 3 && <span className="text-xs md:text-sm text-gold font-medium" data-id="element-216">
                  — ¡Esto es para vos!
                </span>}
            </div>
          </div>
        </div>

        {/* Dynamic Message */}
        <AnimatePresence mode="wait" data-id="element-217">
          {message && <motion.div key={count <= 2 ? 'hint' : count <= 5 ? 'confirm' : count <= 8 ? 'strong' : 'max'} initial={{
          opacity: 0,
          y: 10,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -10,
          scale: 0.97
        }} transition={{
          duration: 0.3
        }} className="text-center mb-5 md:mb-6" data-id="element-218">
              <div className={`inline-block px-4 py-2.5 md:px-6 md:py-3.5 rounded-xl ${message.bg}`} data-id="element-219">
                <p className={`text-sm md:text-base font-bold ${message.textColor}`} data-id="element-220">
                  {message.emoji} {message.text}
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* CTA */}
        <motion.div className="flex justify-center" animate={count >= 3 ? {
        scale: [1, 1.02, 1]
      } : {}} transition={{
        duration: 0.5
      }} data-id="element-221">
          <button onClick={onCtaClick} className={`btn-cta-pulse px-6 md:px-8 py-3.5 md:py-4 rounded-xl text-base md:text-lg tracking-wide w-full md:w-auto font-bold transition-all duration-300 ${count >= 3 ? 'shadow-lg' : ''}`} data-id="element-222">
            QUIERO RECUPERAR EL CONTROL
          </button>
        </motion.div>
      </div>
    </section>;
}